/* eslint-disable max-lines */
//
//  SDKDataCore.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2020 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { DTODesignSystem, DTOPipeline, DTODesignSystemMemberListResponse, DTOFigmaNodeStructureDetailResponse, DTOFigmaNodeStructureListResponse, DTOGetBlockDefinitionsOutput, } from "@supernova-studio/client";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Request engine: Read
export class DataCoreReadRequests {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(bridge) {
        this.bridge = bridge;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Bootstrap data
    async getRemoteBootstrapData(preferredWorkspaceId, preferredDesignSystemId, preferredVersionId, preferredBrandId) {
        let fragment = `bff/app-bootstrap-data`;
        const queryParams = [];
        if (preferredWorkspaceId) {
            queryParams.push(`preferredWorkspaceId=${preferredWorkspaceId}`);
        }
        if (preferredDesignSystemId) {
            queryParams.push(`preferredDesignSystemId=${preferredDesignSystemId}`);
        }
        if (preferredVersionId) {
            queryParams.push(`preferredVersionId=${preferredVersionId}`);
        }
        if (preferredBrandId) {
            queryParams.push(`preferredBrandId=${preferredBrandId}`);
        }
        if (queryParams.length > 0) {
            fragment += `?${queryParams.join("&")}`;
        }
        const endpoint = this.bridge.getBaseEndpoint(fragment);
        const payload = await this.bridge.get(endpoint);
        return payload.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Multiplayer
    /** Remote: Get information about currently logged in user */
    async getRemoteMultiplayerToken(room) {
        const endpoint = this.bridge.getBaseEndpoint("liveblocks/auth");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, {
            room,
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - User
    /** Remote: Get information about currently logged in user */
    async getRemoteCurrentUser() {
        const endpoint = this.bridge.getBaseEndpoint("users/me");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.user;
    }
    /** Remote: Get information about a specific user */
    async getRemoteUser(userId) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const endpoint = this.bridge.getUserEndpoint(userId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.user;
    }
    /** Remote: Get information about all workspaces user can access */
    async getRemoteMemberships(userId) {
        const endpoint = this.bridge.getUserEndpoint(userId, "workspaces");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.membership;
    }
    /** Remote: Get user workspace notification settings */
    async getRemoteUserWorkspaceNotificationSettings(userId, wsId) {
        const endpoint = this.bridge.getUserWorkspaceEndpoint(userId, wsId, "notifications");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result
            .notificationSettings;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Workspaces
    async getRemoteWorkspaceProductFeatures(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "product-features");
        const payload = await this.bridge.get(endpoint);
        return payload.result
            .productFeatures;
    }
    /** Remote: Get information about a specific workspace */
    async getRemoteWorkspaceDetail(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    /** Remote: Get integrations of a specific workspace */
    async getRemoteWorkspaceIntegrations(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "integrations");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    /** Remote: Get redirect url for oauth of given workspace integration */
    async getRemoteWorkspaceIntegrationRedirectUrl({ workspaceId, type, }) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "oauth-requests");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, { type });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    /** Remote: Get workspace's integration git providers data based on the opration
     * * Request Organizations, Repositories, Branches, etc.
     * * This is generic method for all git providers
     */
    async getRemoteWorkspaceIntegrationGitProvidersData({ workspaceId, integrationId, operation, params, }) {
        const endpoint = this.bridge.getWorkspaceIntegrationGitProvidersEndpoint(workspaceId, integrationId, operation);
        // Optional params
        const url = new URL(endpoint);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value) {
                    url.searchParams.append(key, value);
                }
            });
        }
        // Get the full endpoint with the query string
        const endpointWithParams = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpointWithParams);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    /** Remote: Get all users memberships */
    async getRemoteUserMemberships(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "members");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.members;
    }
    /** Remote: Get information about the state of workspace onboarding, what was completed and what not */
    async getRemoteWorkspaceOnboarding(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "onboarding");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.onboardingState;
    }
    /** Remote: Get information about the workspace IP whitelisting configuration */
    async getRemoteWorkspaceIPWhitelistSettings(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "ip-whitelist");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result
            .ipWhitelist;
    }
    async getRemoteWorkspaceNPMRegistrySettings(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "npm-registry");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result
            .npmRegistrySettings;
    }
    async getRemoteWorkspaceNPMRegistryAccessToken(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "npm-registry/access-token");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    async getRemoteWorkspaceSSOSettings(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "sso");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.ssoSettings;
    }
    async getRemoteWorkspaceInvitations(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "invitations");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.invitations;
    }
    async getRemoteWorkspaceInvoices(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "subscription/invoices");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        return payload.result.invoices;
    }
    async getRemoteWorkspaceSubscription(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "subscription");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        const { result } = payload;
        return Object.assign(Object.assign({}, result.subscription), { creditBalance: result.creditBalance });
    }
    async getRemoteWorkspaceAvailableProducts(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "available-plans");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        return payload.result.availablePlans;
    }
    async getRemoteWorkspacePortalSettings(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "portal/settings");
        const payload = await this.bridge.get(endpoint);
        return payload.result.portalSettings;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Design systems
    /** Remote: Get information about one specific design system */
    async getRemoteDesignSystem(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, null);
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return DTODesignSystem.parse(payload.result.designSystem);
    }
    /** Remote: Get information about all design systems under specific workspace */
    async getRemoteDesignSystems(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "design-systems");
        const payload = await this.bridge.get(endpoint);
        const data = payload.result.designSystems;
        return data.map((e) => DTODesignSystem.parse(e));
    }
    /** Remote: Get custom domain */
    async getRemoteCustomDomain(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "custom-domain");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.customDomain;
    }
    /** Remote: Get design system stats */
    async getRemoteDesignSystemStats(dsId, versionId, brandId) {
        const url = new URL(this.bridge.getVersionEndpoint(dsId, versionId, "stats"));
        if (brandId) {
            url.searchParams.set("brandId", brandId);
        }
        const payload = await this.bridge.get(url.toString());
        return payload.result;
    }
    /** Remote: Get a single design source */
    async getRemoteSource(designSystemId, sourceId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `sources/${sourceId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.source;
    }
    /** Remote: Get all design sources of a single design system */
    async getRemoteSources(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "sources");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.sources;
    }
    /** Remote: Get all figma node structures */
    async getFigmaNodeStructures(designSystemId) {
        const payload = await this.bridge.get(this.bridge.getDesignSystemEndpoint(designSystemId, `figma-node-structures`));
        const parsedPayload = DTOFigmaNodeStructureListResponse.parse(payload.result);
        return parsedPayload.structures;
    }
    /** Remote: Get figma node detail */
    async getFigmaNodeStructureDetail(designSystemId, figmaStructureId) {
        const payload = await this.bridge.get(this.bridge.getDesignSystemEndpoint(designSystemId, `figma-node-structures/${figmaStructureId}`));
        const parsedPayload = DTOFigmaNodeStructureDetailResponse.parse(payload.result);
        return parsedPayload.structure;
    }
    /** Remote: Get information about design system's members */
    async getRemoteDesignSystemMemberList(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "members");
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return DTODesignSystemMemberListResponse.parse(payload.result);
    }
    async getRemoteContacts(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "contacts");
        const payload = await this.bridge.get(endpoint);
        return payload.result;
    }
    async getStorybookEntries(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "storybook");
        const payload = await this.bridge.get(endpoint);
        return payload.result.entries;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - DocumentationLegacy
    /** Remote: Get design system documentation url */
    async getRemoteDocumentationUrl(designSystemId, versionId) {
        var _a;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/url");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return (_a = payload.result.url) !== null && _a !== void 0 ? _a : null;
    }
    /** Remote: Get documentation metadata */
    async getRemoteDocumentationStructure(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.documentation;
    }
    /** Remote: Get documentation elements diff */
    async getRemoteDocumentationElementsDiff(designSystemId, versionId, environment) {
        const baseEndpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/elements/diff");
        const params = {
            environment,
        };
        const baseEndpointUrl = new URL(baseEndpoint);
        baseEndpointUrl.searchParams.append("environment", environment);
        const payload = await this.bridge.get(baseEndpointUrl.toString());
        return payload.result;
    }
    /** Remote: Get documentation metadata */
    async getRemoteRealDocumentationStructure(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/structure");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.items;
    }
    /** Remote: Get id of the room for a specific page */
    async getRemoteDocumentationPageRoomId(designSystemId, versionId, pageId) {
        var _a, _b;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `documentation/pages/${pageId}/room`);
        const payload = await this.bridge.get(endpoint);
        return (_b = (_a = payload === null || payload === void 0 ? void 0 : payload.result) === null || _a === void 0 ? void 0 : _a.room) === null || _b === void 0 ? void 0 : _b.id;
    }
    /** Remote: Get id of the room for a specific design system and version. This room contains doc structure */
    async getRemoteDocumentationVersionRoomId(designSystemId, versionId) {
        var _a, _b;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `room`);
        const payload = await this.bridge.get(endpoint);
        return (_b = (_a = payload === null || payload === void 0 ? void 0 : payload.result) === null || _a === void 0 ? void 0 : _a.room) === null || _b === void 0 ? void 0 : _b.id;
    }
    /** Remote: Get id of the room for a specific workspace. */
    async getRemoteDocumentationWorkspaceRoomId(workspaceId) {
        var _a, _b;
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "room");
        const payload = await this.bridge.get(endpoint);
        return (_b = (_a = payload === null || payload === void 0 ? void 0 : payload.result) === null || _a === void 0 ? void 0 : _a.room) === null || _b === void 0 ? void 0 : _b.id;
    }
    async getRemoteDocumentationPageAnchors(designSystemId, versionId, pageId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `documentation/pages/${pageId}/anchors`);
        const payload = await this.bridge.get(endpoint);
        return payload === null || payload === void 0 ? void 0 : payload.result;
    }
    async getRemoteDocumentationPageDependencies(designSystemId, versionId) {
        var _a;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/page-dependencies");
        const payload = await this.bridge.get(endpoint);
        return (_a = payload === null || payload === void 0 ? void 0 : payload.result) === null || _a === void 0 ? void 0 : _a.dependencies;
    }
    /** Remote: Get documentation metadata */
    async getRemoteDocumentationFullLegacyModel(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/all?routingVersion=2");
        const payload = await this.bridge.get(endpoint);
        const groups = payload.result.groups;
        const pages = payload.result.pages;
        return { groups, pages };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Docs analytics
    async getRemoteDocsPageAnalytics(designSystemId, versionId, timeFrames) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/analytics");
        const response = await this.bridge.post(endpoint, {
            timeFrames,
        });
        return response.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Exporters
    /** Remote: Get custom blocks for currently installed documentation exporter under specific ds */
    async getRemoteExporterCustomBlocks(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/custom-blocks");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.customBlocks;
    }
    /** Remote: Get user configuration of specific installed exporter */
    async getRemoteExporterConfigurationUserSpecifiedValues(designSystemId, exporterId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `exporter-properties/${exporterId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.items; // TODO: Proper model
    }
    async getBlockDefinitions(designSystemId, versionId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `versions/${versionId}/documentation/block-definitions?files=true`);
        const payload = await this.bridge.get(endpoint);
        return DTOGetBlockDefinitionsOutput.parse(payload.result).definitions;
    }
    async getUrlPreview(designSystemId, versionId, request) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `versions/${versionId}/documentation/link-preview`);
        const payload = await this.bridge.post(endpoint, request);
        return payload.result;
    }
    /** Remote: Get structural details and metadata about specific installed exporter */
    async getRemoteExporter(workspaceId, exporterId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/exporters/${exporterId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.exporter;
    }
    /** Remote: Get structural details and metadata about all exporters */
    async getRemoteExporters(workspaceId, filter) {
        const endpointBase = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/exporters`);
        const url = new URL(endpointBase);
        if (filter) {
            if (filter.installedOnly) {
                url.searchParams.append("installedOnly", "true");
            }
        }
        url.searchParams.append("limit", "200");
        // Get the full endpoint with the query string
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return {
            exporters: payload.result.exporters,
            membership: payload.result.membership,
        };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resources
    /** Remote: Get all asset resources */
    async getRemoteAssetResources(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "assets?type=image");
        const payload = await this.bridge.get(endpoint);
        return payload.result.assets;
    }
    /** Remote: Get all font resources */
    async getRemoteFontResources(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "assets?type=font");
        const payload = await this.bridge.get(endpoint);
        return payload.result.assets;
    }
    /** Remote: Get all files */
    async getRemoteFiles(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "files");
        const payload = await this.bridge.get(endpoint);
        return payload.result.files;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Tokens
    /** Remote: Get all tokens from all categories */
    async getRemoteTokens(designSystemId, versionId, filter) {
        // Get the base endpoint without the query string
        const endpointBase = this.bridge.getVersionEndpoint(designSystemId, versionId, "tokens");
        // Create a URL object from the base endpoint
        const url = new URL(endpointBase);
        if (filter) {
            // if (filter.type) {
            //   const types = [filter.type] // Prep if we every decide that we want to support multiple types (API supports it)
            //
            //   types.forEach((type) => {
            //     url.searchParams.append("types", type)
            //   })
            // }
            if (filter.brandId) {
                url.searchParams.append("brandId", filter.brandId);
            }
        }
        // Get the full endpoint with the query string
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.tokens;
    }
    /** Remote: Get all token groups from all categories */
    async getRemoteTokenGroups(designSystemId, versionId, filter) {
        const endpointBase = this.bridge.getVersionEndpoint(designSystemId, versionId, "token-groups");
        // Create a URL object from the base endpoint
        const url = new URL(endpointBase);
        if (filter) {
            // if (filter.type) {
            //   url.searchParams.append("elementType", filter.type)
            //
            //   const types = [filter.type] // Prep if we every decide that we want to support multiple types (API however doesn't support it now)
            //
            //   types.forEach((type) => {
            //     url.searchParams.append("elementType", type)
            //   })
            // }
            if (filter.brandId) {
                url.searchParams.append("brandId", filter.brandId);
            }
        }
        // Get the full endpoint with the query string
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.groups;
    }
    /** Remote: Get all token themes from all categories */
    async getRemoteTokenThemes(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "themes");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.themes;
    }
    /** Remote: Get usage of the token across design system */
    async getRemoteTokenUsage(designSystemId, versionId, tokenId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `tokens/${tokenId}/used-by`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Components
    /** Remote: Get all supernova-type components (note: Not design components, those have separate endpoints) */
    async getRemoteComponents(designSystemId, versionId, filter) {
        const endpointBase = this.bridge.getVersionEndpoint(designSystemId, versionId, "design-system-components" // Not a bug, correct endpoint until server serves it right
        );
        const url = new URL(endpointBase);
        if (filter) {
            if (filter.brandId) {
                url.searchParams.append("brandId", filter.brandId);
            }
        }
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.designSystemComponents;
    }
    /** Remote: Get all component groups */
    async getRemoteComponentGroups(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "design-system-component-groups");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.groups;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Design Components
    /** Remote: Get design components (right now, those are only components coming from Figma) */
    async getRemoteDesignComponents(designSystemId, versionId, filter) {
        const endpointBase = this.bridge.getVersionEndpoint(designSystemId, versionId, "components");
        const url = new URL(endpointBase);
        if (filter) {
            if (filter.brandId) {
                url.searchParams.append("brandId", filter.brandId);
            }
        }
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.components;
    }
    /** Remote: Get design component groups (right now, those are only components coming from Figma) */
    async getRemoteDesignComponentGroups(designSystemId, versionId, filter) {
        const endpointBase = this.bridge.getVersionEndpoint(designSystemId, versionId, "component-groups");
        const url = new URL(endpointBase);
        if (filter) {
            if (filter.brandId) {
                url.searchParams.append("brandId", filter.brandId);
            }
        }
        const endpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.groups;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Code Components
    /** Remote: Get code components */
    async getRemoteCodeComponents(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "code-components");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.codeComponents;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Assets
    /** Remote: Get assets (right now, those are only components coming from Figma, but this will change in the future) */
    async getRemoteAssets(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "components");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.components;
    }
    /** Remote: Get design component groups (right now, those are only components coming from Figma, but this will change in the future) */
    async getRemoteAssetGroups(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "component-groups");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.groups;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Elements
    async getRemoteElementPropertyDefinitions(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "element-properties/definitions");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.definitions;
    }
    /** Remote: Get definitions of all element data views. This fetches element views for all types of elements, ie. tokens, components, assets etc. */
    async getRemoteElementDataViewDefinitions(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "element-data-views");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.elementDataViews;
    }
    /** Remote: Get all property values that were set by the user for each elemen */
    async getRemoteElementPropertyValues(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "element-properties/values");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.values;
    }
    async getRemoteFigmaFrames(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "elements?types=FigmaNode");
        const payload = await this.bridge.get(endpoint);
        return payload.result.figmaNodes;
    }
    async getRemoteFigmaFramesV2(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "elements?types=FigmaNode&responseVersion=2");
        const payload = await this.bridge.get(endpoint);
        return payload.result.figmaNodes;
    }
    async getRemoteTokenCollections(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "token-collections");
        const payload = await this.bridge.get(endpoint);
        return payload.result.collections;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Pipelines
    async getRemotePipelines(workspaceId, designSystemId) {
        const endpointBase = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/pipelines?latestJobsLimit=1`);
        const url = new URL(endpointBase);
        if (designSystemId) {
            url.searchParams.append("designSystemId", designSystemId);
        }
        const endpoint = url.toString();
        const payload = await this.bridge.get(endpoint);
        const data = payload.result.pipelines;
        return data.map((pipeline) => DTOPipeline.parse(pipeline));
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Projects
    async getRemoteForgeProjects(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "forge/projects");
        const payload = await this.bridge.get(endpoint);
        return payload.result.projects;
    }
    async getRemoteForgeProjectMemberships(workspaceId, projectId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/memberships`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.memberships;
    }
    async getRemoteForgeProjectContexts(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "forge/project-contexts");
        const payload = await this.bridge.get(endpoint);
        return payload.result.contexts;
    }
    async getRemoteForgeAgents(workspaceId, projectId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/agents`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.agents;
    }
    async getRemoteForgeProjectFeature(workspaceId, projectId, featureId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/features/${featureId}`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.feature;
    }
    async getRemoteForgeProjectFeatures(workspaceId, projectId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/features`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.features;
    }
    async getRemoteForgeProjectArtifact(workspaceId, projectId, artifactId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/artifacts/${artifactId}`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.artifact;
    }
    async getRemoteForgeProjectArtifacts(workspaceId, projectId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/artifacts`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.artifacts;
    }
    async getRemoteForgeProjectRoom(workspaceId, projectId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/room`);
        const response = await this.bridge.get(endpoint);
        return response.result;
    }
    async getRemoteForgeProjectArtifactRoom(workspaceId, projectId, artifactId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/projects/${projectId}/artifacts/${artifactId}/room`);
        const response = await this.bridge.get(endpoint);
        return response.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Builds
    async getRemoteBuild(workspaceId, buildId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/jobs/${buildId}`);
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.job;
    }
    async getRemoteBuilds(workspaceId, versionId, docsEnvironment, limit, offset, scheduleId, exporterId, destinations, createdByUserId, status) {
        const endpointBase = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/jobs`);
        const url = new URL(endpointBase);
        if (limit) {
            url.searchParams.append("limit", limit.toString());
        }
        if (offset) {
            url.searchParams.append("offset", offset.toString());
        }
        if (scheduleId) {
            url.searchParams.append("scheduleId", scheduleId);
        }
        if (exporterId) {
            url.searchParams.append("exporterId", exporterId);
        }
        if (versionId) {
            url.searchParams.append("designSystemVersionId", versionId);
        }
        if (docsEnvironment) {
            url.searchParams.append("docsEnvironment", docsEnvironment);
        }
        if (destinations) {
            destinations.forEach((destination) => {
                url.searchParams.append("destinations[]", destination);
            });
        }
        if (createdByUserId) {
            url.searchParams.append("createdByUserId", createdByUserId);
        }
        if (status) {
            url.searchParams.append("status", status);
        }
        const endpoint = url.toString();
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.jobs;
    }
    async getRemoteBuildLogs(workspaceId, buildId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/jobs/${buildId}/logs`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.logs;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Brands
    async getBrands(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "brands");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.brands;
    }
    async getBrand(designSystemId, versionId, brandId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `brands/${brandId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.brand;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Integrations
    async getAuthenticationTokens() {
        const endpoint = this.bridge.getBaseEndpoint(`auth/tokens`);
        return (await this.bridge.get(endpoint)).result
            .tokens;
    }
    async getOAuthURL(service) {
        const endpoint = this.bridge.getBaseEndpoint(`auth/${service}/oauth-request`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return payload.result.url;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Versions
    async getVersion(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.designSystemVersion;
    }
    async getVersions(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "versions");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result
            .designSystemVersions;
    }
    async getVersionCreationJob(designSystemId, jobId) {
        const endpoint = this.bridge.getVersionCreationJobEndpoint(designSystemId, jobId);
        const payload = await this.bridge.get(endpoint);
        return payload.result.job;
    }
    async getVersionCreationJobs(designSystemId) {
        const endpoint = this.bridge.getVersionCreationJobsEndpoint(designSystemId);
        const payload = await this.bridge.get(endpoint);
        return payload.result.jobs;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Automation
    async getImportJobs(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "import-jobs");
        // Create a URL object from the base endpoint
        const url = new URL(endpoint);
        url.searchParams.append("operation", "Import");
        // Get the full endpoint with the query string
        const queriedEndpoint = url.toString();
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(queriedEndpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.jobs;
    }
    async getImportJob(designSystemId, versionId, jobId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `import-jobs/${jobId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.job;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Storage
    async getKeyValueStorageData(designSystemId, keys) {
        const keyQuery = keys.map((key) => encodeURIComponent(key)).join(",");
        const endpointFragment = `design-systems/${designSystemId}/metadata?keys=${keyQuery}`;
        const endpoint = this.bridge.getBaseEndpoint(endpointFragment);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.metadata;
    }
    async getTokenStudioData(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "bff/token-studio");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.get(endpoint);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return payload.result;
    }
    async getPageRedirects(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `documentation/redirects`);
        const payload = await this.bridge.get(endpoint);
        return payload.result.redirects;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Messaging
    /** Remote: Get all chat threads for a specific workspace */
    async getRemoteChatThreads(workspaceId, limit, offset) {
        const endpointBase = this.bridge.getWorkspaceEndpoint(workspaceId, "forge/threads");
        const url = new URL(endpointBase);
        if (limit) {
            url.searchParams.append("limit", limit.toString());
        }
        if (offset) {
            url.searchParams.append("offset", offset.toString());
        }
        const endpoint = url.toString();
        const result = await this.bridge.get(endpoint);
        return result.result;
    }
    /** Remote: Get a specific chat thread */
    async getRemoteChatThreadMessages(workspaceId, threadId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `forge/threads/${threadId}/messages`);
        const payload = await this.bridge.get(endpoint);
        return payload.result;
    }
}
